import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, Settings, ChevronDown, Zap, Shield, CreditCard, Link2, Wallet, Globe, Users, Search, X, CheckCircle2, Clock, XCircle, Package, Lock, ShoppingCart, Tag, RefreshCw, Star, Mail } from 'lucide-react';
import { pluginsAPI, paymentsAPI, kycAPI, creditsAPI, emailTemplatesAPI } from '../services/api';
import { getPluginConfig, isPluginRegistered, PluginBadge, KycStatsPreview } from '../components/plugins';
import CryptoCheckout from '../components/forms/CryptoCheckout';
import PluginConfigDrawer from '../components/plugins/shared/PluginConfigDrawer.jsx';
import { useNavigate } from 'react-router-dom';

/* ── responsive hook ─────────────────────────────────────────── */
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

/* ── icon map for plugin types ───────────────────────────────── */
const PLUGIN_ICONS = {
  bank_transfer_global: Globe,
  bank_transfer_per_user: Users,
  bank_transfer_per_country: Globe,
  crypto_payments: Wallet,
  payment_links: Link2,
  kyc_verification: Shield,
  credits: CreditCard,
  email_templates: Mail,
  pages: Globe,
};
function getPluginIcon(key) {
  for (const [k, Icon] of Object.entries(PLUGIN_ICONS)) {
    if (key?.startsWith(k) || key === k) return Icon;
  }
  return Package;
}

/* ── status pill ─────────────────────────────────────────────── */
function StatusPill({ isActive, expired, isPurchased }) {
  if (!isPurchased) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)', color: 'var(--accent-primary)' }}>
      <ShoppingCart className="w-3 h-3" /> Available
    </span>
  );
  if (expired) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)' }}>
      <XCircle className="w-3 h-3" /> Expired
    </span>
  );
  if (isActive) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)', color: 'var(--accent-success)' }}>
      <CheckCircle2 className="w-3 h-3" /> Active
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--text-tertiary) 12%, transparent)', color: 'var(--text-tertiary)' }}>
      <Clock className="w-3 h-3" /> Inactive
    </span>
  );
}

/* ── price badge ─────────────────────────────────────────────── */
function PriceBadge({ plugin }) {
  const isFree = plugin.is_free || parseFloat(plugin.price || 0) === 0;
  const cycle = plugin.billing_cycle;

  if (isFree) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)', color: 'var(--accent-success)' }}>
      <Star className="w-3 h-3" /> Free
    </span>
  );

  const cycleLabel = cycle === 'monthly' ? '/mo' : cycle === 'yearly' ? '/yr' : '';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
      style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 12%, transparent)', color: 'var(--accent-warning)' }}>
      <Tag className="w-3 h-3" /> ${parseFloat(plugin.price).toFixed(2)}{cycleLabel}
    </span>
  );
}

/* ── toggle switch ───────────────────────────────────────────── */
function ToggleSwitch({ active, onToggle, disabled = false, size = 'md' }) {
  const w = size === 'sm' ? '2.25rem' : '2.75rem';
  const h = size === 'sm' ? '1.25rem' : '1.5rem';
  const dot = size === 'sm' ? '0.875rem' : '1.125rem';
  const travel = size === 'sm' ? '1rem' : '1.25rem';
  return (
    <button type="button" onClick={disabled ? undefined : onToggle}
      className="relative rounded-full transition-all duration-300 ease-out"
      style={{
        width: w, height: h,
        backgroundColor: active ? 'var(--accent-success)' : 'var(--bg-tertiary)',
        boxShadow: active ? '0 0 8px color-mix(in srgb, var(--accent-success) 40%, transparent)' : 'inset 0 1px 3px rgba(0,0,0,0.15)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}>
      <span className="absolute rounded-full bg-white shadow-sm transition-all duration-300 ease-out"
        style={{
          width: dot, height: dot,
          top: '50%', left: '3px',
          transform: active ? `translate(${travel}, -50%)` : 'translate(0, -50%)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
    </button>
  );
}

/* ── animation wrapper ───────────────────────────────────────── */
function FadeIn({ children, delay = 0, className = '' }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
    }}>{children}</div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   HELPER: Determine plugin purchase/active state
   ═══════════════════════════════════════════════════════════════════ */
function getPluginState(p) {
  const pluginKey = p.pluginKey || p.plugin_key;
  const isPurchased = !!p.purchased_at || !!p.broker_plugin_id;
  const isFree = p.is_free || parseFloat(p.price || 0) === 0;
  const expired = p.expires_at && new Date(p.expires_at) < new Date();
  const isActive = p.is_active && !expired;
  const canToggle = (isPurchased || isFree) && !expired;
  const canConfigure = (isPurchased || isFree) && !expired;
  const needsPurchase = !isPurchased && !isFree;
  const needsRenewal = isPurchased && expired;

  return { pluginKey, isPurchased, isFree, expired, isActive, canToggle, canConfigure, needsPurchase, needsRenewal };
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function BrokerPluginsPage() {
  const isMobile = useIsMobile();

  const [plugins, setPlugins] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [credits, setCredits] = useState({ rules: [], history: [] });
  const [kycConfig, setKycConfig] = useState(null);
  const [kycStats, setKycStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | inactive | available

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPlugin, setDrawerPlugin] = useState(null);

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutPlugin, setCheckoutPlugin] = useState(null);

  const navigate = useNavigate();
  // const configRef = useRef(null);

  /* ── data fetching with individual error handling ───────────────────────────────────────── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch plugins data first (critical)
      const pluginData = await pluginsAPI.getAll();

      // Fetch all other data in parallel with individual error handling
      const [bankResult, cryptoResult, linksResult, creditsResult] = await Promise.allSettled([
        paymentsAPI.getBankAccounts(),
        paymentsAPI.getCryptoWallets(),
        paymentsAPI.getPaymentLinks(),
        Promise.all([creditsAPI.getRules(), creditsAPI.getAllCredits()]).catch(e => {
          console.warn('Credits API failed:', e);
          return [{ rules: [] }, { credits: [] }];
        }),
      ]);

      // Extract successful results or use defaults
      const b = bankResult.status === 'fulfilled' ? bankResult.value : [];
      const c = cryptoResult.status === 'fulfilled' ? cryptoResult.value : [];
      const l = linksResult.status === 'fulfilled' ? linksResult.value : [];
      const cr = creditsResult.status === 'fulfilled' ? creditsResult.value : [{ rules: [] }, { credits: [] }];

      // Log any failures (optional)
      if (bankResult.status === 'rejected') console.warn('Bank accounts fetch failed:', bankResult.reason);
      if (cryptoResult.status === 'rejected') console.warn('Crypto wallets fetch failed:', cryptoResult.reason);
      if (linksResult.status === 'rejected') console.warn('Payment links fetch failed:', linksResult.reason);

      // Merge purchased + available plugins
      const purchasedMap = {};
      (pluginData.purchased || []).forEach(p => { purchasedMap[p.plugin_key] = p; });

      const allPlugins = [
        ...(pluginData.purchased || []),
        ...(pluginData.available || [])
          .filter(p => !purchasedMap[p.plugin_key])
          .map(p => ({
            ...p,
            pluginKey: p.plugin_key,
            is_active: false,
            purchased_at: null,
            expires_at: null,
            broker_plugin_id: null,
          }))
      ];

      setPlugins(allPlugins);
      setBankAccounts(b || []);
      setCryptoWallets(c || []);
      setPaymentLinks(l || []);
      setCredits({
        rules: cr[0]?.rules || cr[0] || [],
        history: cr[1]?.credits || cr[1] || []
      });

    } catch (e) {
      setError(e.message || 'Failed to load plugins');
      console.error('Critical plugin fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchKycData = useCallback(async () => {
    try {
      const [configRes, statsRes] = await Promise.all([kycAPI.getConfig(), kycAPI.getStatistics()]);
      const config = configRes.data?.data || configRes.data || configRes;
      const stats = statsRes.data?.data || statsRes.data || statsRes;
      setKycConfig(config);
      setKycStats(stats);
    } catch (e) {
      console.error('Failed to load KYC data:', e);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const kycPlugin = plugins.find(p => (p.pluginKey || p.plugin_key) === 'kyc_verification');
    if (kycPlugin?.is_active) fetchKycData();
  }, [plugins, fetchKycData]);

  /* ── handlers ────────────────────────────────────────────── */
  const togglePlugin = async (pluginKey, currentlyActive) => {
    setError(null);
    try {
      if (currentlyActive) await pluginsAPI.disable(pluginKey);
      else await pluginsAPI.enable(pluginKey);
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to toggle plugin');
    }
  };

  const handleBuyClick = (plugin) => {
    setCheckoutPlugin(plugin);
    setShowCheckout(true);
  };

  const handlePurchaseSuccess = () => {
    setShowCheckout(false);
    setCheckoutPlugin(null);
    fetchAll();
  };

const saveBankAccount = async (id, form, targets) => {
  try {
    const pluginTypeMap = { 
      bank_transfer_global: 'global', 
      bank_transfer_per_user: 'per_user', 
      bank_transfer_per_country: 'per_country' 
    };
    
    // Get the current plugin from drawer instead of selectedPlugin
    const currentPluginKey = drawerPlugin?.pluginKey || drawerPlugin?.plugin_key;
    const formWithType = { ...form, pluginType: pluginTypeMap[currentPluginKey] || 'global' };
    
    let accountId = id;
    if (id) {
      await paymentsAPI.updateBankAccount(id, formWithType);
    } else {
      const res = await paymentsAPI.createBankAccount(formWithType);
      accountId = res?.id || res?.data?.id;
      if (!accountId) throw new Error('Failed to create bank account - no ID returned');
    }
    
    if (targets && accountId) {
      if (currentPluginKey === 'bank_transfer_per_user') {
        const existing = (bankAccounts.find(a => a.id === accountId)?.assignedUsers || []).map(u => u.user_id);
        for (const uid of targets) { 
          if (!existing.includes(uid)) await paymentsAPI.assignBankToUser(accountId, { userId: uid }); 
        }
        for (const uid of existing) { 
          if (!targets.includes(uid)) await paymentsAPI.removeUserAssignment(accountId, uid); 
        }
      }
      if (currentPluginKey === 'bank_transfer_per_country') {
        const existing = (bankAccounts.find(a => a.id === accountId)?.mappedCountries || []).map(c => c.country_code);
        for (const cc of targets) { 
          if (!existing.includes(cc)) await paymentsAPI.mapBankToCountry(accountId, { countryCode: cc }); 
        }
        for (const cc of existing) { 
          if (!targets.includes(cc)) await paymentsAPI.removeCountryMapping(accountId, cc); 
        }
      }
    }
    await fetchAll();
  } catch (e) { setError(e.message); }
};

  const deleteBankAccount = async (id) => { try { await paymentsAPI.deleteBankAccount(id); await fetchAll(); } catch (e) { setError(e.message); } };
  const removeUserAssignment = async (bankId, userId) => { try { await paymentsAPI.removeUserAssignment(bankId, userId); await fetchAll(); } catch (e) { setError(e.message); } };
  const removeCountryMapping = async (bankId, cc) => { try { await paymentsAPI.removeCountryMapping(bankId, cc); await fetchAll(); } catch (e) { setError(e.message); } };
  const saveCryptoWallet = async (id, form) => { try { if (id) await paymentsAPI.updateCryptoWallet(id, form); else await paymentsAPI.createCryptoWallet(form); await fetchAll(); } catch (e) { setError(e.message); } };
  const deleteCryptoWallet = async (id) => { try { await paymentsAPI.deleteCryptoWallet(id); await fetchAll(); } catch (e) { setError(e.message); } };
  const savePaymentLink = async (id, form) => { try { if (id) await paymentsAPI.updatePaymentLink(id, form); else await paymentsAPI.createPaymentLink(form); await fetchAll(); } catch (e) { setError(e.message); } };
  const deletePaymentLink = async (id) => { try { await paymentsAPI.deletePaymentLink(id); await fetchAll(); } catch (e) { setError(e.message); } };

  /* ── renderConfig ────────────────────────────────────────── */
const renderConfig = (pluginKey) => {
  console.log('Rendering config for:', pluginKey);
  console.log('Is registered?', isPluginRegistered(pluginKey));
  console.log('Config:', getPluginConfig(pluginKey));
  
  if (!isPluginRegistered(pluginKey)) {
    return (
      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        No configuration panel available for this plugin.
      </div>
    );
  }

  const config = getPluginConfig(pluginKey);
  const PanelComponent = config.component;

  const handlers = {
    onSave: pluginKey.startsWith('bank_transfer') ? saveBankAccount : pluginKey === 'crypto_payments' ? saveCryptoWallet : pluginKey === 'payment_links' ? savePaymentLink : undefined,
    onDelete: pluginKey.startsWith('bank_transfer') ? deleteBankAccount : pluginKey === 'crypto_payments' ? deleteCryptoWallet : pluginKey === 'payment_links' ? deletePaymentLink : undefined,
    onRemoveUser: pluginKey === 'bank_transfer_per_user' ? removeUserAssignment : undefined,
    onRemoveCountry: pluginKey === 'bank_transfer_per_country' ? removeCountryMapping : undefined,
  };

  // ✅ KYC Plugin
  if (pluginKey === 'kyc_verification') {
    return (
      <div className="space-y-3">
        <div>
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Current Status</p>
          <KycStatsPreview stats={kycStats} />
        </div>
        <PanelComponent config={kycConfig} onUpdate={async (updates) => {
          try { await kycAPI.updateConfig(updates); await fetchKycData(); }
          catch (e) { setError('Failed to update KYC configuration'); }
        }} />
      </div>
    );
  }

  // ✅ Email Templates Plugin (self-contained, no external data needed)
  if (pluginKey === 'email_templates') {
    return <PanelComponent />;
  }

  // ✅ Bank Transfer Plugins
  if (pluginKey.startsWith('bank_transfer')) {
    return <PanelComponent accounts={config.filterData(bankAccounts)} {...handlers} />;
  }

  // ✅ Crypto Payments
  if (pluginKey === 'crypto_payments') {
    return <PanelComponent wallets={cryptoWallets} {...handlers} />;
  }

  // ✅ Payment Links
  if (pluginKey === 'payment_links') {
    return <PanelComponent links={paymentLinks} {...handlers} />;
  }

  // ✅ Credits Plugin
  if (pluginKey === 'credits') {
    return <PanelComponent credits={credits} onRefresh={fetchAll} />;
  }

  return null;
};

  /* ── filtered plugins ────────────────────────────────────── */
  const filtered = plugins.filter(p => {
    const key = p.pluginKey || p.plugin_key;
    const name = (p.label || p.name || '').toLowerCase();
    const desc = (p.description || '').toLowerCase();
    const q = search.toLowerCase();
    const matchSearch = !q || name.includes(q) || desc.includes(q) || key.includes(q);

    const state = getPluginState(p);
    const matchFilter =
      filter === 'all' ||
      (filter === 'active' && state.isActive) ||
      (filter === 'inactive' && state.isPurchased && !state.isActive) ||
      (filter === 'available' && !state.isPurchased);

    return matchSearch && matchFilter;
  });

  const activeCount = plugins.filter(p => getPluginState(p).isActive).length;
  const purchasedCount = plugins.filter(p => getPluginState(p).isPurchased).length;
  const availableCount = plugins.filter(p => !getPluginState(p).isPurchased).length;

  /* ── scroll to config on select ──────────────────────────── */
  // useEffect(() => {
  //   if (selectedPlugin && configRef.current && !isMobile) {
  //     configRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  //   }
  // }, [selectedPlugin, isMobile]);

  /* ── loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Loading plugins…</p>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════════
     SHARED: Plugin Card (used in both mobile + desktop)
     ═══════════════════════════════════════════════════════════════ */
    const PluginCard = ({ p, idx, layout = 'list', navigate }) => { 
      const state = getPluginState(p);
      const { pluginKey, isPurchased, isFree, expired, isActive, canToggle, canConfigure, needsPurchase, needsRenewal } = state;
      const Icon = getPluginIcon(pluginKey);
      const isGrid = layout === 'grid';
      
      // ✅ ADD THIS LINE - Get plugin config
      const config = getPluginConfig(pluginKey);

      const handleCardClick = () => {
        if (canConfigure) {
          if (config?.fullPage) {
            navigate(`/plugins/${pluginKey}`);
          } else {
            setDrawerPlugin(p);
            setDrawerOpen(true);
          }
        }
      };

      const handleConfigClick = (e) => {
        e.stopPropagation();
        if (config?.fullPage) {
          navigate(`/plugins/${pluginKey}`);
        } else {
          setDrawerPlugin(p);
          setDrawerOpen(true);
        }
      };

    return (
      <FadeIn key={pluginKey} delay={idx * 50}>
        <div className={`rounded-2xl border overflow-hidden transition-all duration-300 ${!isPurchased ? 'relative' : ''}`}
          style={{
            backgroundColor: isPurchased ? 'var(--bg-secondary)' : 'var(--bg-primary)',
            borderColor: !isPurchased ? 'color-mix(in srgb, var(--border-color) 60%, transparent)' : 'var(--border-color)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            opacity: !isPurchased ? 0.85 : 1,
          }}>

          {/* Locked overlay for unpurchased */}
          {!isPurchased && !isFree && (
            <div className="absolute top-3 right-3 z-10">
              <div className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)' }}>
                <Lock className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
              </div>
            </div>
          )}

          <div className={`${isGrid ? 'p-5' : 'px-4 py-4'} ${canConfigure ? 'cursor-pointer' : ''}`}
            onClick={canConfigure ? handleCardClick : undefined}>

            {/* Top row: icon + name + status */}
            <div className={`flex items-start gap-3 ${isGrid ? 'mb-3' : 'mb-2.5'}`}>
              <div className={`${isGrid ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl flex items-center justify-center shrink-0`} style={{
                backgroundColor: isActive
                  ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)'
                  : !isPurchased
                    ? 'color-mix(in srgb, var(--text-tertiary) 6%, transparent)'
                    : 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)',
              }}>
                <Icon style={{
                  width: isGrid ? '20px' : '18px', height: isGrid ? '20px' : '18px',
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-tertiary)'
                }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className={`${isGrid ? 'text-sm' : 'text-sm'} font-bold truncate`}
                    style={{ color: isPurchased ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    {p.label || p.name}
                  </p>
                  {!isGrid && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <StatusPill isActive={isActive} expired={expired} isPurchased={isPurchased || isFree} />
                    </div>
                  )}
                </div>
                <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                  {p.description}
                </p>
              </div>
            </div>

            {/* Grid: status badge row */}
            {isGrid && (
              <div className="flex items-center gap-2 mb-3">
                <StatusPill isActive={isActive} expired={expired} isPurchased={isPurchased || isFree} />
                <PriceBadge plugin={p} />
              </div>
            )}

            {/* Action row: toggle/buy + price */}
            <div className={`flex items-center justify-between pt-2.5 border-t`} style={{ borderColor: 'var(--border-color)' }}>

              {/* Left side: toggle or buy button */}
              {isPurchased || isFree ? (
                <div className="flex items-center gap-2.5" onClick={e => e.stopPropagation()}>
                  <ToggleSwitch active={isActive} size="sm" disabled={!canToggle}
                    onToggle={() => togglePlugin(pluginKey, isActive)} />
                  <span className="text-xs font-medium" style={{ color: isActive ? 'var(--accent-success)' : 'var(--text-tertiary)' }}>
                    {isActive ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ) : (
                <button onClick={e => { e.stopPropagation(); handleBuyClick(p); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{
                    backgroundColor: 'var(--accent-primary)',
                    color: '#fff',
                  }}>
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Buy — ${parseFloat(p.price).toFixed(2)}
                  {p.billing_cycle === 'monthly' ? '/mo' : p.billing_cycle === 'yearly' ? '/yr' : ''}
                </button>
              )}

              {/* Right side: price (list) or config button */}
              <div className="flex items-center gap-2">
                {!isGrid && <PriceBadge plugin={p} />}

                {/* Renewal button for expired subscriptions */}
                {needsRenewal && (
                  <button onClick={e => { e.stopPropagation(); handleBuyClick(p); }}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
                      color: 'var(--accent-warning)',
                    }}>
                    <RefreshCw className="w-3 h-3" /> Renew
                  </button>
                )}

                {/* Config button */}
                {canConfigure && (
                  <button
                    onClick={handleConfigClick}
                    className="p-1.5 rounded-lg transition-all hover:scale-110 relative group"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
                      color: 'var(--accent-primary)',
                    }}
                    title={config?.fullPage ? 'Open in full page' : 'Configure'}
                  >
                    <Settings className="w-3.5 h-3.5" />
                    {config?.fullPage && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
                        style={{ backgroundColor: 'var(--accent-warning)' }} />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Expiry date */}
            {isPurchased && p.expires_at && (
              <p className="text-[11px] mt-2 font-medium" style={{ color: expired ? 'var(--accent-danger)' : 'var(--text-tertiary)' }}>
                {expired ? 'Expired' : 'Expires'} {new Date(p.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* REMOVED: Inline config panel - now using drawer */}
        </div>
      </FadeIn>
    );
  };

  /* ═════════════════════════════════════════════════════════════════
     MOBILE LAYOUT
     ═════════════════════════════════════════════════════════════════ */
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* Hero */}
        <div className="relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
          <div className="absolute -bottom-12 -left-6 w-24 h-24 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }} />

          <div className="relative px-5 pt-6 pb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <Zap className="w-4 h-4 text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.7)' }}>Plugins</p>
            </div>

            <div className="flex items-end gap-4">
              <div>
                <p className="text-[42px] font-black text-white leading-none tabular-nums">{activeCount}</p>
                <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>of {purchasedCount} purchased active</p>
              </div>

              <div className="flex gap-2 mb-1.5 ml-auto">
                {[
                  { label: 'Active', val: activeCount, },
                  { label: 'Owned', val: purchasedCount, },
                  { label: 'Store', val: availableCount, },
                ].map(s => (
                  <div key={s.label} className="px-2.5 py-1.5 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                    <p className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>{s.label}</p>
                    <p className="text-sm font-bold text-white tabular-nums">{s.val}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pt-4 pb-8 space-y-3">

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search plugins…"
              className="w-full pl-9 pr-9 py-2.5 rounded-xl text-sm border outline-none transition-all"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: search ? 'var(--accent-primary)' : 'var(--border-color)',
                color: 'var(--text-primary)',
              }} />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              </button>
            )}
          </div>

          {/* Filter chips */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { key: 'all', label: `All (${plugins.length})` },
              { key: 'active', label: `Active (${activeCount})` },
              { key: 'inactive', label: `Owned (${purchasedCount - activeCount})` },
              { key: 'available', label: `Store (${availableCount})` },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold uppercase tracking-wide transition-all whitespace-nowrap"
                style={{
                  backgroundColor: filter === f.key ? 'color-mix(in srgb, var(--accent-primary) 15%, transparent)' : 'var(--bg-secondary)',
                  color: filter === f.key ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                  borderWidth: '1px', borderStyle: 'solid',
                  borderColor: filter === f.key ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'var(--border-color)',
                }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Error */}
          {error && (
            <FadeIn>
              <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-xs border"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 20%, transparent)', color: 'var(--accent-danger)' }}>
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="flex-1">{error}</span>
                <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
              </div>
            </FadeIn>
          )}

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="flex flex-col items-center py-10 gap-2">
              <Package className="w-10 h-10" style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>No plugins found</p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Try adjusting your search or filter</p>
            </div>
          )}

          {/* Plugin cards */}
          {filtered.map((p, idx) => (
             <PluginCard key={p.pluginKey || p.plugin_key} p={p} idx={idx} layout="list" navigate={navigate} />
          ))}
        </div>

        <div className="pb-[env(safe-area-inset-bottom)]" />

        {/* Plugin Config Drawer */}
        <PluginConfigDrawer
          plugin={drawerPlugin}
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setDrawerPlugin(null);
          }}
        >
          {drawerPlugin && renderConfig(drawerPlugin.pluginKey || drawerPlugin.plugin_key)}
        </PluginConfigDrawer>

        {/* Checkout Modal */}
        {showCheckout && checkoutPlugin && (
          <CryptoCheckout
            plugin={checkoutPlugin}
            onClose={() => { setShowCheckout(false); setCheckoutPlugin(null); }}
            onSuccess={handlePurchaseSuccess}
          />
        )}
      </div>
    );
  }

  /* ═════════════════════════════════════════════════════════════════
     DESKTOP LAYOUT
     ═════════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">

      {/* Header */}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, var(--accent-primary), color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)))',
            }}>
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Plugins</h1>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {activeCount} active · {purchasedCount} owned · {availableCount} in store
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            {[
              { label: 'Active', val: activeCount, color: 'var(--accent-success)' },
              { label: 'Owned', val: purchasedCount, color: 'var(--accent-primary)' },
              { label: 'Store', val: availableCount, color: 'var(--accent-warning)' },
            ].map(s => (
              <div key={s.label} className="px-4 py-2.5 rounded-xl border" style={{
                backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)',
              }}>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--text-tertiary)' }}>{s.label}</p>
                <p className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.val}</p>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* Search + Filter */}
      <FadeIn delay={50}>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search plugins…"
              className="w-full pl-9 pr-9 py-2 rounded-xl text-sm border outline-none transition-all"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: search ? 'var(--accent-primary)' : 'var(--border-color)',
                color: 'var(--text-primary)',
              }} />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              </button>
            )}
          </div>
          <div className="flex gap-1.5 p-1 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'inactive', label: 'Owned' },
              { key: 'available', label: 'Store' },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  backgroundColor: filter === f.key ? 'var(--accent-primary)' : 'transparent',
                  color: filter === f.key ? '#fff' : 'var(--text-secondary)',
                }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* Error */}
      {error && (
        <FadeIn>
          <div className="flex items-center gap-2.5 border rounded-xl px-4 py-3 text-sm"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 20%, transparent)', color: 'var(--accent-danger)' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        </FadeIn>
      )}

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3">
          <Package className="w-12 h-12" style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>No plugins match your criteria</p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Try a different search or filter</p>
        </div>
      )}

      {/* Plugin grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p, idx) => (
           <PluginCard key={p.pluginKey || p.plugin_key} p={p} idx={idx} layout="grid" navigate={navigate} />
        ))}
      </div>

      {/* Config panel (desktop) */}
      <PluginConfigDrawer
        plugin={drawerPlugin}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerPlugin(null);
        }}
      >
        {drawerPlugin && renderConfig(drawerPlugin.pluginKey || drawerPlugin.plugin_key)}
      </PluginConfigDrawer>

      {/* Checkout Modal */}
      {showCheckout && checkoutPlugin && (
        <CryptoCheckout
          plugin={checkoutPlugin}
          onClose={() => { setShowCheckout(false); setCheckoutPlugin(null); }}
          onSuccess={handlePurchaseSuccess}
        />
      )}
    </div>
  );
}